import createClient from "openapi-fetch";
import type { paths } from "./schema";
import { cacheBackendToken } from "@/lib/tokenCache";

export const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "";

/**
 * Typed openapi-fetch client for the Centring backend API.
 * Auth header is injected via middleware.
 */
export const fetchClient = createClient<paths>({
  baseUrl: BACKEND_URL,
});

// --- Silent MS token refresh when backend signals expiry ---
//
// When the backend returns X-MS-Auth-Status: token_expired (503), the
// stored MS Graph token can no longer call Graph API (expired or
// undecryptable from a prior session/deployment).
//
// Strategy: the middleware THROWS on token_expired responses so TanStack
// Query treats the fetch as an error. Combined with placeholderData:
// keepPreviousData on Graph-proxied queries, the previous data stays
// visible while the silent refresh runs. After refresh completes,
// queries are invalidated with refetchType: "active" to immediately
// re-fetch with the fresh token.
//
// Three-layer guard (all state on globalThis to survive Vite HMR):
//
// 1. Promise dedup -- concurrent callers share the same in-flight Promise.
//
// 2. Cooldown (30 s) -- after a completed refresh, suppress further
//    attempts.
//
// 3. Circuit breaker (max 2 per 60 s) -- if the backend keeps returning
//    token_expired (e.g., stored refresh_token encrypted with a rotated
//    Fernet key), stop retrying until the next window opens.
//
// HMR safety: openapi-fetch `use()` stacks middleware; Vite HMR
// re-executes this module without ejecting old handlers. We store the
// middleware reference on globalThis and eject the previous one before
// re-registering, so only ONE handler is active at a time.

interface MsRefreshState {
  promise: Promise<void> | null;
  lastDone: number;
  attempts: number;
  windowStart: number;
}

const _state: MsRefreshState = ((globalThis as Record<string, unknown>).__msRefreshState ??= {
  promise: null,
  lastDone: 0,
  attempts: 0,
  windowStart: 0,
}) as MsRefreshState;

const MS_REFRESH_COOLDOWN_MS = 30_000; // 30 seconds
const MS_REFRESH_MAX_ATTEMPTS = 2;
const MS_REFRESH_WINDOW_MS = 60_000; // 1 minute

async function _silentlyRefreshMsToken(): Promise<void> {
  // Dedup: join in-flight refresh if one is already running
  if (_state.promise) return _state.promise;

  // Cooldown: skip if we completed a refresh recently
  if (Date.now() - _state.lastDone < MS_REFRESH_COOLDOWN_MS) return;

  // Circuit breaker: stop if we've exceeded max attempts in the window
  const now = Date.now();
  if (now - _state.windowStart > MS_REFRESH_WINDOW_MS) {
    _state.attempts = 0;
    _state.windowStart = now;
  }
  if (_state.attempts >= MS_REFRESH_MAX_ATTEMPTS) return;

  _state.attempts++;

  _state.promise = (async () => {
    try {
      const { msalInstance } = await import("@/lib/msal/instance");
      const { tokenRequest } = await import("@/lib/msalConfig");
      const { queryClient } = await import("@/lib/queryClient");

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        // Backend OAuth flow -- MSAL has no accounts, so client-side silent
        // refresh is impossible. Don't invalidate queries here: without a
        // fresh token, refetches would just produce more 503s and waste the
        // circuit breaker budget. Let TanStack Query's error/stale state
        // handle it; the user will need to re-login.
        return;
      }

      const msalResponse = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: accounts[0],
      });

      if (!msalResponse.accessToken) return;

      // Send fresh token to backend to update stored credentials
      const res = await fetch(`${BACKEND_URL}/api/auth/microsoft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken: msalResponse.accessToken }),
      });

      if (!res.ok) return;

      // Store the refreshed backend JWT so subsequent requests use it
      try {
        const resJson = await res.json();
        const newToken = resJson?.data?.token;
        if (newToken) {
          cacheBackendToken(newToken);
        }
      } catch {
        // Response parsing failed -- token refresh still succeeded on backend
      }

      // Invalidate and immediately refetch active Graph-proxied queries.
      // The fresh token is now stored on the backend, so refetches will
      // succeed. The circuit breaker prevents cascade loops if the token
      // is still invalid (e.g., rotated Fernet key).
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/v1/meetings"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/v1/people"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/v1/files"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/v1/sharepoint"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/v1/onedrive"],
        refetchType: "active",
      });
    } catch {
      // Silent refresh failed -- user will need to re-login manually
    } finally {
      _state.lastDone = Date.now();
      _state.promise = null;
    }
  })();

  return _state.promise;
}

// --- Middleware registration (HMR-safe) ---
// Eject any previously registered middleware before adding a new one.
// Without this, Vite HMR re-executes the module and stacks duplicate
// handlers -- each with its own closure -- causing N-fold POST bursts.

const _prevMiddleware = (globalThis as Record<string, unknown>).__fetchClientMiddleware;
if (_prevMiddleware) {
  fetchClient.eject(_prevMiddleware as Parameters<typeof fetchClient.eject>[0]);
}

const _middleware = {
  async onRequest({ request }: { request: Request }) {
    const token = localStorage.getItem("backendToken");
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return request;
  },
  async onResponse({ request, response }: { request: Request; response: Response }) {
    // Backend signals that the stored MS Graph token is expired (503).
    // Kick off silent refresh, then throw so TanStack Query treats this
    // as an error -- placeholderData: keepPreviousData will preserve the
    // previous data while the refresh runs and queries re-fetch.
    if (response.headers.get("X-MS-Auth-Status") === "token_expired") {
      _silentlyRefreshMsToken();
      throw new Error("MS Graph token expired -- refreshing silently");
    }
    if (response.status === 401) {
      // Only auto-logout if we actually sent an auth token that was rejected.
      // If no token was sent, the user is still in the login flow (backend
      // token exchange hasn't completed yet) -- don't force a logout.
      if (request.headers.has("Authorization")) {
        const { handle401Error } = await import("@/lib/apiErrorHandler");
        handle401Error();
      }
    }
    // 403 = authenticated but not authorized (missing tenant, wrong role, etc.)
    // Do NOT clear tokens or redirect -- let the caller handle the error.
    return response;
  },
};

fetchClient.use(_middleware);
(globalThis as Record<string, unknown>).__fetchClientMiddleware = _middleware;

// Vite HMR: eject middleware when this module is about to be replaced
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    fetchClient.eject(_middleware);
  });
}
